/**
 * Custom Size measurements modal (markup in snippets/custom-size-measurements.liquid).
 *
 * Loaded on every page from snippets/scripts.liquid: the modal also appears inside quick add modals on pages that
 * don't render the product form themselves (collections), where a snippet-level script would not be loaded.
 */
if (!customElements.get('custom-size-measurements')) {
  const CM_PER_INCH = 2.54;

  /**
   * Custom Size measurements modal.
   * While a "Custom" variant is selected, adding to cart opens the modal until the required measurements are saved.
   * Saved measurements are written as hidden `properties[...]` inputs into the product form.
   */
  class CustomSizeMeasurements extends HTMLElement {
    /** @type {AbortController | undefined} */
    #controller;

    /**
     * The product form the measurements are added to. Looked up on every use: the quick add modal can rebuild
     * (morph) the form while this element stays in place, so a stored reference would go stale.
     * Prefers the form next to this element, because in the quick add modal the same form id can exist twice on
     * the page; the mobile quick add modal drops the buy buttons wrapper, so the modal content is searched too.
     * @returns {HTMLFormElement | null}
     */
    get cartForm() {
      const form =
        this.closest('.buy-buttons-block')?.querySelector('form[data-type="add-to-cart-form"]') ??
        this.closest('.quick-add-modal__content')?.querySelector('form[data-type="add-to-cart-form"]') ??
        document.getElementById(this.dataset.formId ?? '');
      return form instanceof HTMLFormElement ? form : null;
    }

    /** The container in the product form that receives the `properties[...]` inputs */
    get properties() {
      return this.cartForm?.querySelector('[data-custom-size-properties]') ?? null;
    }

    /** The element that gets `data-custom-size-active` (hides express checkout) */
    get buyButtons() {
      return this.closest('.buy-buttons-block') ?? this.cartForm?.closest('product-form-component') ?? null;
    }

    connectedCallback() {
      if (this.#controller) return;

      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-measurements-form]');
      if (!this.cartForm || !this.dialog || !this.form) return;

      this.fields = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('[data-measurement]')]);
      this.unitInput = this.querySelector('[data-unit-input]');
      this.unit = 'cm';
      this.saved = false;
      this.active = false;
      this.activeField = null;

      this.#controller = new AbortController();
      const { signal } = this.#controller;
      const section = this.closest('.shopify-section') ?? document;

      // A "Custom" size counts as selected right away; the variant update below then confirms the selection
      section.addEventListener(
        'change',
        (event) => {
          const target = event.target;
          if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
          if (!target.closest('variant-picker') || !/custom/i.test(target.value)) return;

          this.#setActive(true);
        },
        { signal }
      );

      // Every click on the Custom Size button opens the modal, also when it's already selected
      // (clicking a checked radio doesn't fire `change`)
      section.addEventListener(
        'click',
        (event) => {
          const target = event.target;
          if (!(target instanceof HTMLInputElement) || !target.closest('variant-picker')) return;
          if (!/custom/i.test(target.value)) return;

          this.#setActive(true);
          this.open();
        },
        { signal }
      );

      section.addEventListener(
        'variant:update',
        (event) => {
          const variantId = /** @type {CustomEvent} */ (event).detail?.resource?.id;
          // Let the product form update its variant id first
          queueMicrotask(() => this.#sync(variantId));
        },
        { signal }
      );

      // Adding a Custom Size to the cart needs saved measurements first
      window.addEventListener(
        'click',
        (event) => {
          const button = event.target instanceof Element ? event.target.closest('button') : null;
          const cartForm = this.cartForm;
          if (!(button instanceof HTMLButtonElement) || !cartForm || button.form !== cartForm) return;
          if (button.type !== 'submit') return;

          // The form may have been rebuilt since the last variant update, so check what is selected right now
          this.#sync();
          if (!this.active || this.#isComplete()) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          this.open({ fromAddToCart: true });
        },
        { signal, capture: true }
      );

      window.addEventListener(
        'submit',
        (event) => {
          if (event.target !== this.cartForm) return;

          this.#sync();
          if (!this.active || this.#isComplete()) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          this.open({ fromAddToCart: true });
        },
        { signal, capture: true }
      );

      this.querySelector('[data-close]')?.addEventListener('click', () => this.dialog?.close(), { signal });

      // Clicking the backdrop closes the modal
      this.dialog.addEventListener(
        'click',
        (event) => {
          if (event.target !== this.dialog || !this.dialog) return;
          const rect = this.dialog.getBoundingClientRect();
          const isOutside =
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom;
          if (isOutside) this.dialog.close();
        },
        { signal }
      );

      this.dialog.addEventListener('close', () => document.documentElement.style.removeProperty('overflow'), {
        signal,
      });

      this.form.addEventListener(
        'focusin',
        (event) => {
          if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-measurement]')) return;
          this.activeField = event.target;
          this.#updateGuide();
        },
        { signal }
      );

      this.form.addEventListener(
        'input',
        (event) => {
          if (event.target instanceof HTMLInputElement && event.target.matches('[data-measurement]')) {
            const field = event.target;
            // Keep the exact value in cm, so switching units back and forth doesn't drift
            field.dataset.valueCm = Number.isFinite(field.valueAsNumber)
              ? String(this.unit === 'in' ? field.valueAsNumber * CM_PER_INCH : field.valueAsNumber)
              : '';
            this.#clearError(field);
          }
          this.saved = false;
          this.#update();
        },
        { signal }
      );

      this.querySelectorAll('[data-set-unit]').forEach((button) => {
        button.addEventListener('click', () => this.#setUnit(button.getAttribute('data-set-unit') ?? 'cm'), {
          signal,
        });
      });

      // The mouse wheel scrolls the modal instead of changing a focused number field
      this.form.addEventListener(
        'wheel',
        (event) => {
          const target = event.target;
          if (!(target instanceof HTMLInputElement) || target.type !== 'number' || document.activeElement !== target) return;

          event.preventDefault();
          this.querySelector('.custom-size__body')?.scrollBy({ top: event.deltaY, left: event.deltaX });
        },
        { signal, passive: false }
      );

      this.form.addEventListener(
        'submit',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.#save();
        },
        { signal }
      );

      this.#sync();
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#controller = undefined;
    }

    /**
     * @param {{ fromAddToCart?: boolean }} [options] - `fromAddToCart` explains why the modal opened instead of adding
     */
    open({ fromAddToCart = false } = {}) {
      if (!this.dialog || this.dialog.open) return;

      const notice = this.querySelector('[data-notice]');
      if (notice instanceof HTMLElement) notice.hidden = !fromAddToCart;

      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.querySelector('[data-heading]')?.focus({ preventScroll: true });
    }

    /** @param {string | number} [variantId] */
    #sync(variantId) {
      const id = String(variantId ?? this.cartForm?.elements.namedItem('id')?.value ?? '');
      // Read the ids each time: the element's attributes can be updated in place (morph)
      const customVariantIds = (this.dataset.customVariantIds ?? '').split(',').filter(Boolean);
      this.#setActive(customVariantIds.includes(id));
      if (!this.active && this.dialog?.open) this.dialog.close();
    }

    /** @param {boolean} active */
    #setActive(active) {
      this.active = active;
      this.buyButtons?.toggleAttribute('data-custom-size-active', active);
      this.#update();
    }

    #isComplete() {
      return this.saved && this.fields.every((field) => field.checkValidity());
    }

    #save() {
      const invalidFields = this.fields.filter((field) => !this.#validate(field));
      if (invalidFields.length) {
        invalidFields[0].focus();
        return;
      }

      this.saved = true;
      this.#update();
      this.dialog?.close();

      // Continue with the add to cart the customer started
      const addButton = this.cartForm?.querySelector('button[type="submit"][name="add"]');
      if (this.active && addButton instanceof HTMLButtonElement && !addButton.disabled) addButton.click();
    }

    #update() {
      const required = this.fields.filter((field) => field.required);
      const completed = required.filter((field) => field.value && field.checkValidity()).length;
      const progress = this.querySelector('[data-progress]');
      if (progress) progress.textContent = `${completed} of ${required.length} required measurements completed`;

      // Cart line properties, only for a Custom Size with saved measurements
      const properties = this.properties;
      if (properties) {
        properties.replaceChildren();

        if (this.active && this.#isComplete()) {
          for (const field of this.fields) {
            if (!field.value) continue;
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = `properties[${field.name}]`;
            input.value = `${field.value} ${this.unit}`;
            properties.append(input);
          }
        }
      }

      this.#updateGuide();
    }

    /** @param {string} unit */
    #setUnit(unit) {
      if (unit === this.unit) return;

      const divisor = unit === 'in' ? CM_PER_INCH : 1;

      for (const field of this.fields) {
        const valueCm = Number(field.dataset.valueCm);
        if (field.value && field.dataset.valueCm && Number.isFinite(valueCm)) {
          field.value = String(Math.round((valueCm / divisor) * 10) / 10);
        }
        field.min = String(Math.floor((Number(field.dataset.minCm) / divisor) * 10) / 10);
        field.max = String(Math.ceil((Number(field.dataset.maxCm) / divisor) * 10) / 10);
        this.#clearError(field);
      }

      this.unit = unit;
      if (this.unitInput instanceof HTMLInputElement) this.unitInput.value = unit;
      this.querySelectorAll('[data-unit-label]').forEach((label) => (label.textContent = unit));
      this.querySelectorAll('[data-set-unit]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.getAttribute('data-set-unit') === unit));
      });

      this.saved = false;
      this.#update();
    }

    /** @param {HTMLInputElement} field */
    #validate(field) {
      const valid = field.checkValidity();
      const error = field.closest('.custom-size__field')?.querySelector('[data-error]');

      field.setAttribute('aria-invalid', String(!valid));
      if (error instanceof HTMLElement) {
        error.hidden = valid;
        error.textContent = valid
          ? ''
          : field.validity.valueMissing && !field.validity.badInput
            ? 'Please enter this measurement.'
            : `Enter a value between ${field.min} and ${field.max} ${this.unit}.`;
      }

      return valid;
    }

    /** @param {HTMLInputElement} field */
    #clearError(field) {
      field.removeAttribute('aria-invalid');
      const error = field.closest('.custom-size__field')?.querySelector('[data-error]');
      if (error instanceof HTMLElement) {
        error.hidden = true;
        error.textContent = '';
      }
    }

    #updateGuide() {
      const field = this.activeField;
      const view = field?.name === 'Back length' ? 'back' : 'front';

      this.querySelectorAll('[data-body-view]').forEach((part) => {
        if (part instanceof SVGElement) part.style.display = part.dataset.bodyView === view ? '' : 'none';
      });
      const showHeels = field?.name === 'Waist down with heels';
      this.querySelectorAll('[data-heels]').forEach((part) => {
        if (part instanceof SVGElement) part.style.display = showHeels ? '' : 'none';
      });
      this.querySelectorAll('[data-feet]').forEach((part) => {
        if (part instanceof SVGElement) part.style.display = showHeels ? 'none' : '';
      });
      this.querySelector('[data-body-model]')?.setAttribute(
        'aria-label',
        `${view === 'back' ? 'Back' : 'Front'} view showing measurement positions`
      );
      this.querySelectorAll('[data-guide-region]').forEach((region) => {
        region.classList.toggle('is-active', region.getAttribute('data-guide-region') === field?.name);
      });

      const label = this.querySelector('[data-guide-label]');
      const value = this.querySelector('[data-guide-value]');
      if (label) label.textContent = field?.name ?? 'Select a measurement';
      if (value) value.textContent = field?.value ? `${field.value} ${this.unit}` : '—';
    }
  }

  customElements.define('custom-size-measurements', CustomSizeMeasurements);
}
